import { numberWithCommas } from "@fla/common";
import classNames from "classnames";
import { FC, ReactNode, useState } from "react";
import { Controller } from "react-hook-form";
import styled from "styled-components";

import { PasswordHideIcon, PasswordShowIcon } from "../../SvgIcon";
import { FormError } from "../FormError";
import { FormLabel } from "../FormLabel";

const TextField = styled.input<{
  theme: "light" | "dark";
  is_amount: "true" |"false";
}>`
  padding-left: ${props => (props.is_amount==="true" ? "25px" : "10px")};
  border: ${props => (props.theme === "light" ? "1px solid #f2f2f2" : "1px solid #fff")};
  background: ${props => (props.theme === "light" ? "#f2f2f2" : "#fff")};
  height: 38px;
  font-size: 14px;
  color: #333;
  border-radius: 10px;
  box-shadow: none;
  transition: background-color 0.2s ease-in-out, border-color 0.2s ease-in-out;
  outline: none;

  &:focus {
    background: #fff;
    border: 1px solid #4c9aff;
  }

  &:hover {
    background: #e9ebef;
    border: 1px solid #dee0e6;

    &:focus-within {
      background: #fff;
      border: 1px solid #4c9aff;
    }
  }
`;

type TextInputProps = {
  title?: ReactNode;
  titleClasses?: string;
  subTitle?: string;
  subTitleClasses?: string;
  type?: "text" | "password" | "email" | "date";
  name: string;
  id: string;
  control: any;
  placeholder?: string;
  disabled?: boolean;
  readOnly?: boolean;
  isAmount?: boolean;
  isPercentage?: boolean;
  onChange?: (val: any) => void;
  wrapClasses?: string;
  theme?: "light" | "dark";
};

export const TextInput: FC<TextInputProps> = ({
  title,
  titleClasses,
  subTitle,
  subTitleClasses,
  type = "text",
  name,
  control,
  placeholder,
  disabled = false,
  readOnly = false,
  isAmount = false,
  isPercentage = false,
  onChange: _onChange,
  wrapClasses = "",
  theme = "light"
}) => {
  const [toggleOn, setToggleOn] = useState(false);
  const onToggleClick = () => setToggleOn(!toggleOn);

  return (
    <article id={name} className={wrapClasses}>
      {title ? (
        <FormLabel htmlFor={name} id={name} className={titleClasses}>
          {title}
        </FormLabel>
      ) : null}
      {subTitle ? (
        <p className={`text-sm font-light italic text-gray-500 mt-2 mb-4 ${subTitleClasses}`}>{subTitle}</p>
      ) : null}

      <div className="w-full relative">
        {isAmount ? <p className="pr-2 font-light text-lg absolute ml-2 mt-1 text-gray-400">$</p> : null}
        <article className="flex items-center">
          <Controller
            name={name}
            control={control}
            render={({ field: { onChange, onBlur, value, ref }, fieldState: { error } }) => {
              return (
                <section
                  className={classNames("flex flex-col w-full", {
                    "w-2/5": isPercentage
                  })}
                >
                  <TextField
                    ref={ref}
                    is_amount={isAmount?"true":"false"}
                    type={toggleOn ? "text" : type}
                    placeholder={placeholder}
                    disabled={disabled}
                    readOnly={readOnly}
                    value={isAmount && !isNaN(value) ? (value ? numberWithCommas(value) : "") : value}
                    onChange={ev => {
                      const val = ev.target.value;
                      if (isAmount && typeof val === "string" && val.includes(",")) {
                        onChange(val.replace(/[^0-9\.]+/g, ""));
                      } else onChange(val);

                      if (typeof _onChange === "function") _onChange(val);
                    }}
                    onBlur={onBlur}
                    className={type === "password" ? "pr-6" : ""}
                    // @ts-ignore
                    theme={theme}
                  />
                  {type === "password" ? (
                    <div className="absolute right-0 w-6 h-full flex flex-col justify-center">
                      {toggleOn ? (
                        <button type="button" onClick={onToggleClick}>
                          <PasswordShowIcon strokeColor="text-gray-500" />
                        </button>
                      ) : (
                        <button type="button" onClick={onToggleClick}>
                          <PasswordHideIcon strokeColor="text-gray-500" />
                        </button>
                      )}
                    </div>
                  ) : null}
                  {error ? <FormError error={error?.message} id={name} /> : null}
                </section>
              );
            }}
          />
          {isPercentage ? <p className="pl-5 text-lg">%</p> : null}
        </article>
      </div>
    </article>
  );
};
